import { _ } from "../../../../core/polyglot/polyglot.js";
import { select } from "../../../../core/elements/index.js";
import AbstractFormulaTokenVisitor from "../../../../models/keyFigure/formulaToken/visitors/AbstractFormulaTokenVisitor.js";
import ClosingBalanceTokenPeriodFilter from "../../../../models/keyFigure/formulaToken/periodFilters/ClosingBalanceTokenPeriodFilter.js";
import IndexPicker from "../../../shared/IndexPicker/IndexPicker.js";
import KeyFigureSelectionWidget from "../../../../core/widgets/keyFigures/KeyFigureSelectionWidget/KeyFigureSelectionWidget.js";
import multipleDimensionsPickerWidget from "../../../../core/widgets/multipleDimensionsPickerWidget.js";
import NullTokenPeriodFilter from "../../../../models/keyFigure/formulaToken/periodFilters/NullTokenPeriodFilter.js";
import OpeningBalanceTokenPeriodFilter from "../../../../models/keyFigure/formulaToken/periodFilters/OpeningBalanceTokenPeriodFilter.js";
import PeriodTypePicker from "../../../shared/PeriodTypePicker/PeriodTypePicker.js";
import popoverActionButtonWidget from "../../../../core/widgets/popoverActionButtonWidget.js";
import SwitchButton from "../../../../core/widgets/SwitchButton/SwitchButton.js";
import visibleKeyFigures from "../../../../models/keyFigure/preset/visibleKeyFigures.js";

/**
 * Visitor that renders actions (for the right-hand menu) for a formula token.
 *
 * @param {Dimension} dimension - Dimension tree that is used to specify
 * dimension specifications on formula tokens.
 * @param {PeriodType} currentPeriodTypeId - Period type of the currently selected period.
 * It acts as a default value for the dropdown.
 * @param {boolean} isBimestersEnabled - True when the Bimester feature is activated.
 */
export default class FormulaTokenActionsRenderer extends AbstractFormulaTokenVisitor {
	constructor({
		dimensionTree,
		currentPeriodTypeId,
		isBimestersEnabled,
	} = {}) {
		super(...arguments);
		this._dimensionTree = dimensionTree;
		this._shiftPeriodTypeId = currentPeriodTypeId;
		this._isBimestersEnabled = isBimestersEnabled;
		this._periodTypeId = currentPeriodTypeId;
		this._rollingPeriodTypeId = currentPeriodTypeId;
	}

	tokenActionsRenderer(token) {
		return (document) =>
			this.visit(token).map((renderer) => renderer(document));
	}

	visitAbstractFormulaToken(token) {
		return [this._deleteTokenButtonRenderer(token)];
	}

	visitAbstractValueFormulaToken(token) {
		return [
			this._creditPositiveSwitchRenderer(token),
			this._periodFilterDropdownRenderer(token),
			this._dimensionsPickerRenderer(token),
			this._rollingSwitchRenderer(token),
			this._rollingPickerRenderer(token),
			this._periodTypePickerRenderer(token),
			this._periodShifterRenderer(token),
			...super.visitAbstractValueFormulaToken(token),
		];
	}

	visitAccountFormulaToken(token) {
		return [
			this._editTokenButtonRenderer(token),
			...super.visitAccountFormulaToken(token),
		];
	}

	visitAccountGroupFormulaToken(token) {
		return [
			this._editTokenButtonRenderer(token),
			...super.visitAccountGroupFormulaToken(token),
		];
	}

	visitAccountIntervalFormulaToken(token) {
		return [
			this._editTokenButtonRenderer(token),
			...super.visitAccountIntervalFormulaToken(token),
		];
	}

	visitConstantFormulaToken(token) {
		return [
			this._editTokenButtonRenderer(token),
			...super.visitConstantFormulaToken(token),
		];
	}

	visitKeyFigureFormulaToken(token) {
		return [
			this._keyFigureSelectionButtonRenderer(token),
			...super.visitKeyFigureFormulaToken(token),
		];
	}

	_deleteTokenButtonRenderer(token) {
		return (document) => (html) => {
			html.button(
				{
					klass: "btn btn-danger btn-block",
					"data-test": "delete-token",
					click: document.deleteFormulaToken.bind(document, token),
				},
				_("Delete"),
			);
		};
	}

	_editTokenButtonRenderer(token) {
		return (document) => (html) => {
			html.button(
				{
					klass: "btn btn-default btn-block",
					"data-test": "edit-token",
					click: () => {
						document.showItemEditionDialog({
							formulaToken: token,
							callback: document.tokenEdited.bind(
								document,
								token,
							),
						});
					},
				},
				_("Edit {token}", { token: token.getLabel() }),
			);
		};
	}

	_creditPositiveSwitchRenderer(token) {
		return (document) =>
			this._switchRenderer({
				state: token.isCreditPositive(),
				dataTestName: "toggle-credit-positive",
				action: (state) => {
					token.setCreditPositive(state);
					document.tokenEdited(token);
				},
				label: _("Credit Positive"),
			});
	}

	_periodFilterDropdownRenderer(token) {
		return (document) => (html) => {
			const periodFilters = [
				NullTokenPeriodFilter,
				OpeningBalanceTokenPeriodFilter,
				ClosingBalanceTokenPeriodFilter,
			].map((constructor) => constructor.instance());

			const elements = periodFilters.map((filter) => ({
				value: filter,
				label: filter.getLabel(),
			}));

			elements[0].selected = true;

			html.div(
				{ klass: "right-menu-block" },
				html.span({ klass: "switch-label" }, _("Period filter…")),
				select({
					items: elements,
					selected: token.getPeriodFilter(),
					onChange: (value) => {
						token.setPeriodFilter(value);
						document.tokenEdited(token);
					},
				}),
			);
		};
	}

	_dimensionsPickerRenderer(token) {
		return (document) => (html) => {
			const dimensionPicker = multipleDimensionsPickerWidget({
				dimensionTree: this._dimensionTree,
				dimensionSpecification: token.getDimensionSpecification(),
			});

			dimensionPicker.onDimensionsSelected.register(
				(dimensionSpecification) => {
					token.setDimensionSpecification(dimensionSpecification);
					document.tokenEdited(token);
				},
			);

			html.div(
				{ klass: "right-menu-block" },
				html.span({ klass: "switch-label" }, _("Filter dimensions…")),
				dimensionPicker,
			);
		};
	}

	_instantiatePeriodTypePicker(spec) {
		return PeriodTypePicker.forCommonTypes({
			isBimesterEnabled: this._isBimestersEnabled,
			spec,
		});
	}

	_periodTypePickerRenderer(token) {
		return (document) => (html) => {
			const selectedPeriodTypeId =
				token.getPeriodType()?.getId() || this._periodTypeId;

			const periodTypeDropdown = this._instantiatePeriodTypePicker({
				selectedPeriodTypeId,
				onChange: (periodType) => {
					token.setPeriodType(periodType);
					document.tokenEdited(token);

					this._shiftPeriodTypeDropdown.setMinimumPeriodType(
						periodType,
					);
				},
			});

			html.div(
				{ klass: "right-menu-block", "data-test": "period-type" },
				html.span({ klass: "switch-label" }, _("Period type")),
				html.div(periodTypeDropdown),
			);
		};
	}

	_periodShifterRenderer(token) {
		return (document) => (html) => {
			const shiftPeriodPicker = new IndexPicker({
				index: token.getShift(),
				callback: (index) => {
					token.setShift(index);
					document.tokenEdited(token);
				},
			});

			this._shiftPeriodTypeDropdown = this._instantiatePeriodTypePicker({
				selectedPeriodTypeId:
					token.getShiftPeriodType()?.getId() ||
					this._shiftPeriodTypeId,
				onInitialize: (periodType) =>
					token.setShiftPeriodType(periodType),
				onChange: (periodType) => {
					token.setShiftPeriodType(periodType);
					document.tokenEdited(token);
				},
			});

			html.div(
				{ klass: "right-menu-block", "data-test": "shift-period" },
				html.span({ klass: "switch-label" }, _("Shift period")),
				html.div(
					{ klass: "flex-container" },
					shiftPeriodPicker,
					this._shiftPeriodTypeDropdown,
				),
			);
		};
	}

	_rollingSwitchRenderer(token) {
		return (document) =>
			this._switchRenderer({
				state: token.isRolling(),
				dataTestName: "toggle-rolling",
				action: (state) => {
					token.setIsRolling(state);
					document.tokenEdited(token);
					document.updateActions();
				},
				label: _("Rolling period"),
			});
	}

	_rollingPickerRenderer(token) {
		return (document) => (html) => {
			if (!token.isRolling()) {
				return;
			}

			const rollingPeriodPicker = new IndexPicker({
				min: 2,
				index: token.getRollingLength(),
				callback: (index) => {
					token.setRollingLength(index);
					document.tokenEdited(token);
				},
			});

			const rollingPeriodTypeDropdown = this._instantiatePeriodTypePicker(
				{
					selectedPeriodTypeId:
						token.getRollingPeriodType()?.getId() ||
						this._rollingPeriodTypeId,
					onInitialize: (periodType) => {
						token.setRollingPeriodType(periodType);
						document.tokenEdited(token);
					},
					onChange: (periodType) => {
						token.setRollingPeriodType(periodType);
						document.tokenEdited(token);
					},
				},
			);

			html.div(
				{ klass: "right-menu-block", "data-test": "rolling-period" },
				html.div(
					{ klass: "flex-container" },
					rollingPeriodPicker,
					rollingPeriodTypeDropdown,
				),
			);
		};
	}

	_keyFigureSelectionButtonRenderer(token) {
		return (document) => (html) => {
			const keyFigurePicker = new KeyFigureSelectionWidget();

			keyFigurePicker.setup({
				keyFigures: visibleKeyFigures.getKeyFigures(),
				presets: [document.getVisibleKeyFigures()],
			});

			keyFigurePicker.selectKeyFigure(token.getKeyFigure());

			const label = _("Change KPI");

			html.render(
				popoverActionButtonWidget({
					label,
					popoverHeader: label,
					popoverPrimaryButtonLabel: label,
					popoverContent: keyFigurePicker,
					popoverAction: () => {
						token.setKeyFigure(keyFigurePicker.getSelection());
						document.tokenEdited(token);
					},
				}),
			);
		};
	}

	_switchRenderer({ state, action, label, dataTestName }) {
		return (html) => {
			const button = new SwitchButton({
				state,
				dataTestName,
			});

			button.stateChanged.register(action);

			html.div(
				{ klass: "right-menu-block" },
				html.span({ klass: "switch-label" }, label),
				button,
			);
		};
	}
}
